import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/system";
import { Typography, Button, Tooltip, Alert } from "@mui/material";
import { useState } from "react";
import { dataManagementTabs } from "./DataManagementTabs";
import VersionHistory from "../../data-management/components/VersionHistory/VersionHistory";
import DataExtraction from "../../data-management/components/DataExtraction/DataExtraction";
import UploadIngestModal from "../../data-management/components/UploadIngestModal/UploadIngestModal";
import { CustomTabs } from "components/common/CustomTabs/CustomTabs";
import { CustomTabPanel } from "components/common/CustomTabs/CustomTabPanel";
import useDocumentHead from "utils/useDocumentHead";
import { CustomIcon, Icons } from "components/common";
import { useGetVersionHistoryIngestGet } from "orval/generated/endpoint";
import { useMemberInfo } from "../../../core/MemberInfoProvider/MemberInfoProvider";

function DataManagementPage() {
  const { t } = useTranslation("productConfiguration");
  const { palette } = useTheme();
  const [value, setValue] = useState(0);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [versionHistoryKey, setVersionHistoryKey] = useState(0);
  
  // Get ingest data to check for running ingests
  const { data: ingestData } = useGetVersionHistoryIngestGet();
  
  // Get member info to check if user is PwC staff
  const { memberInfo } = useMemberInfo();
  
  // Check if any ingest is currently running
  const hasRunningIngest = ingestData?.some(ingest => ingest.status === "running") || false;
  
  const isPwCStaff = memberInfo?.email?.includes("@pwc.com") || false;
  const hasFailedIngest = ingestData?.some((ingest) => ingest.status === "failed") || false;
  
  useDocumentHead({
    titleAsTranslationKey: t(
      "common:documentHeads:dataManagementVersionHistory",
    ),
  });

  const handleOpenUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const handleUploadSuccess = () => {
    // Force refresh of VersionHistory component by changing its key
    setVersionHistoryKey((prev: number) => prev + 1);
    // Switch to Version History tab to show the new ingest
    setValue(0);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "27px",
        width: "100%",
        height: "100%",
      }}
    >
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", ml: -1 }}>
            <CustomIcon
              name={Icons.DATABASE}
              width={"40px"}
              height={"40px"}
              fill={palette.primary.main}
            />
            <Typography
              className={"header-H2"}
              pr="5px"
              fontWeight="500"
              tabIndex={0}
            >
              {t("dataManagementPage.title")}
            </Typography>
          </Box>

          {isPwCStaff && (
            <Tooltip
              title={hasRunningIngest ? "Cannot start new ingest while another is running" : ""}
              placement="bottom"
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    <CustomIcon
                      name={Icons.UPLOAD}
                      width="16px"
                      height="16px"
                      fill="white"
                    />
                  }
                  onClick={handleOpenUploadModal}
                  disabled={hasRunningIngest}
                  sx={{ px: 2, py: 0.75, height: "36px" }}
                >
                  Start new ingest
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        <Typography mt={2} variant="body1" tabIndex={0}>
          {t("dataManagementPage.description")}
        </Typography>

        {hasRunningIngest && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Non-PwC users are temporarily locked out while the ingest is in progress.{" "}
            Access returns automatically once this ingest completes successfully.
          </Alert>
        )}

        {!hasRunningIngest && hasFailedIngest && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Non-PwC users remain locked out because the last ingest failed. Start a new ingest to restore access once it finishes successfully.
          </Alert>
        )}
      </Box>
      <Box>
        <Box borderBottom={1} borderColor="divider">
          <CustomTabs
            tabList={dataManagementTabs.tabs}
            value={value}
            setValue={setValue}
          />
        </Box>
        <CustomTabPanel value={value} index={0}>
          <VersionHistory 
            key={versionHistoryKey}
            onSwitchToUpload={handleOpenUploadModal} 
          />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <DataExtraction />
        </CustomTabPanel>
      </Box>

      <UploadIngestModal
        open={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        onSuccess={handleUploadSuccess}
        ingestData={ingestData}
      />
    </Box>
  );
}

export default DataManagementPage;
